import { Search } from "@mui/icons-material";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tags } from "../../assets/dummyData";
import { motion, AnimatePresence } from "framer-motion";
import { colors } from "../../theme/theme";

const HomePageSearchBar = ({ search, setSearch }) => {
  const navigate = useNavigate();

  const [index, setIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [showText, setShowText] = useState(true);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    const currentTag = tags[index].name;
    setTypedText("");
    setShowText(true);

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      setTypedText(currentTag.slice(0, charIndex + 1));
      charIndex++;
      if (charIndex === currentTag.length) {
        clearInterval(typingInterval);
        setTimeout(() => setShowText(false), 1000);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, [index]);

  useEffect(() => {
    if (!showText) {
      const timeout = setTimeout(() => {
        setIndex((prev) => (prev + 1) % tags.length);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [showText]);

  const handleSearch = () => {
    if (search.trim().length > 0) {
      navigate(`/search?q=${encodeURIComponent(search)}`);
    }
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)",
        px: isMobile ? 2.5 : 4,
        pt: isMobile ? 6 : 8,
        pb: isMobile ? 5 : 6,
        mb: 0,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: isMobile ? "400px" : "600px",
          height: isMobile ? "400px" : "600px",
          background: "radial-gradient(circle, rgba(255,183,156,0.08) 0%, rgba(255,183,156,0) 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "900px",
          mx: "auto",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Main Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins-Bold",
              fontSize: isMobile ? "36px" : isTablet ? "48px" : "58px",
              fontWeight: 800,
              color: "#1a1a1a",
              lineHeight: 1.15,
              mb: isMobile ? 2.5 : 3,
              letterSpacing: "-0.04em",
            }}
          >
            Describe what you're{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #FFB79C 0%, #FF9B80 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              looking for
            </Box>
          </Typography>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box
            sx={{
              position: "relative",
              maxWidth: "650px",
              mx: "auto",
            }}
          >
            <TextField
              variant="outlined"
              placeholder=""
              value={search}
              autoComplete="off"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  height: isMobile ? "58px" : "66px",
                  backgroundColor: "#FFFFFF",
                  fontSize: isMobile ? "15px" : "16px",
                  border: "2px solid #E5E7EB",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "& input": {
                    paddingLeft: "28px",
                    paddingRight: "90px",
                    fontFamily: "Poppins-Regular",
                  },
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&.Mui-focused": {
                    borderColor: "#FFB79C",
                    boxShadow: "0 8px 24px rgba(255,183,156,0.15)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                  },
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={handleSearch}
                    style={{ cursor: "pointer", marginRight: "10px" }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? "46px" : "52px",
                        height: isMobile ? "46px" : "52px",
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #FFB79C 0%, #FF9B80 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                        boxShadow: "0 2px 12px rgba(255,183,156,0.3)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #FFA785 0%, #FF8A6D 100%)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 16px rgba(255,183,156,0.4)",
                        },
                        "&:active": {
                          transform: "translateY(0px)",
                        },
                      }}
                    >
                      <Search sx={{ color: "#fff", fontSize: isMobile ? "22px" : "24px" }} />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />

            {!search && (
              <Box
                sx={{
                  position: "absolute",
                  left: "24px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aaa",
                  pointerEvents: "none",
                  overflow: "hidden",
                  height: "1.9em",
                  fontSize: isMobile ? "15px" : "16px",
                }}
              >
                <AnimatePresence mode="wait">
                  {showText && (
                    <motion.span
                      key={index}
                      initial={{ y: "100%", opacity: 0 }}
                      animate={{ y: "0%", opacity: 1 }}
                      exit={{ y: "-100%", opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      style={{
                        display: "block",
                        whiteSpace: "nowrap",
                        fontFamily: "Poppins-Regular",
                      }}
                    >
                      {typedText}
                      <motion.span
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        style={{ display: "inline-block" }}
                      >
                        |
                      </motion.span>
                    </motion.span>
                  )}
                </AnimatePresence>
              </Box>
            )}
          </Box>
        </motion.div>

        {/* Image Search Tile */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
        >
          <Box
            onClick={() => navigate("/image-search")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.25,
              mt: isMobile ? 2 : 2.5,
              px: 2.5,
              py: 1.25,
              borderRadius: "14px",
              border: "1.5px solid #E5E7EB",
              backgroundColor: "#fff",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              "&:hover": {
                borderColor: "#FFB79C",
                boxShadow: "0 6px 20px rgba(255,183,156,0.18)",
                transform: "translateY(-2px)",
                "& .image-search-icon": {
                  background:
                    "linear-gradient(135deg, #FFB79C 0%, #FF9B80 100%)",
                  "& svg": {
                    color: "#fff",
                  },
                },
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            <Box
              className="image-search-icon"
              sx={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                background:
                  "linear-gradient(135deg, rgba(255,183,156,0.15) 0%, rgba(255,155,128,0.15) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
              }}
            >
              <Box
                component="svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                sx={{
                  width: "18px",
                  height: "18px",
                  color: "#FFB79C",
                  transition: "color 0.3s ease",
                }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </Box>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  lineHeight: 1.3,
                }}
              >
                Search by Image
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins-Regular",
                  fontSize: "11px",
                  color: "#999",
                  lineHeight: 1.3,
                }}
              >
                Upload a photo to find products
              </Typography>
            </Box>

            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{
                width: "16px",
                height: "16px",
                color: "#ccc",
                ml: 0.5,
              }}
            >
              <polyline points="9 18 15 12 9 6" />
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default HomePageSearchBar;
